import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, projectStatusLabel, useI18n } from '../lib/i18n';
import { formatDate } from '../lib/format';
import {
  MAX_LINK_NAME_LENGTH,
  MAX_LINK_URL_LENGTH,
  cleanLinks,
  linkUrlProblem,
  type ProjectLink,
} from '../lib/project-links';
import { IN_PROGRESS_STATUSES, NO_SUPPORT, PROJECT_STATUSES, type ProjectSupport } from '../lib/project-status';
import { FormGrid, FormPage } from '../components/FormPage';
import { Button, Field, Input, Select, Textarea, useToast } from '../components/ui';

/** One row of the Links list; `key` keeps a row's inputs its own while others come and go. */
interface LinkRow extends ProjectLink {
  key: number;
}

let nextLinkKey = 1;
const linkRow = (name = '', url = ''): LinkRow => ({ key: nextLinkKey++, name, url });

/**
 * Creating and editing a project, as a page.
 *
 * Editing matters for CR-001 §49: projects that predate the CR keep
 * `clientId = NULL` — nothing is attributed automatically — so this form is the
 * only way one ever gains a client.
 *
 * Routes: `/projects/new` and `/projects/:id/edit`.
 */
export function ProjectFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.get<any>(`/projects/${id}`),
    enabled: editing,
  });

  const clients = useQuery({
    queryKey: ['client-options'],
    queryFn: () => api.get<any[]>('/clients/options'),
    enabled: can(P.CLIENTS_VIEW),
  });

  const { data: users } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
  });

  const [form, setForm] = useState({
    name: '',
    nameAr: '',
    code: '',
    description: '',
    ownerUserId: '',
    clientId: '',
    status: 'ACTIVE',
    priority: 'MEDIUM',
    startDate: '',
    expectedEndDate: '',
  });

  // A new project is offered the last code + 1 (operator, 2026-10-07): asked
  // for afresh on every visit, and filled in once, only into an empty field —
  // a reminder of where the codes are up to, changed like any other field.
  const nextCode = useQuery({
    queryKey: ['projects', 'next-code'],
    queryFn: () => api.get<{ code: string | null; after: string | null }>('/projects/next-code'),
    enabled: !editing,
    gcTime: 0,
  });
  const [offered, setOffered] = useState(false);
  if (!editing && nextCode.data && !offered) {
    setOffered(true);
    if (!form.code && nextCode.data.code) setForm({ ...form, code: nextCode.data.code });
  }

  // Every status is offered (2026-10-09). A finished project is in support
  // while one of its contracts runs, so Completed waits until none does; Support
  // with no contract running is support by hand. The API settles both the same
  // way on save, and the project page's Change status offers the same.
  const support: ProjectSupport = existing.data?.support ?? NO_SUPPORT;
  const running = support.contracts.length > 0;

  // Its links (2026-10-26): as many as wanted, each under its own name. There is
  // always a row to type in; a row left blank is not saved.
  const [links, setLinks] = useState<LinkRow[]>(() => [linkRow()]);
  // The links the form opened with, as they would be sent: saved only once
  // someone changes them, as the status below, so a save never puts back links
  // someone else changed meanwhile.
  const [openedLinks, setOpenedLinks] = useState('[]');
  // A row's mistakes show once Save has been pressed.
  const [tried, setTried] = useState(false);

  const [loaded, setLoaded] = useState(false);
  // The status the form opened with: saved only once someone changes it, so a
  // contract that started or ended while the form was open is never overruled.
  const [openedWith, setOpenedWith] = useState<string | null>(null);
  // The end date it opened with, likewise: a contract that starts sets it (2026-10-27).
  const [openedEnd, setOpenedEnd] = useState('');
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const p = existing.data;
    const status = p.status === 'COMPLETED' && running ? 'SUPPORT' : p.status ?? 'ACTIVE';
    setOpenedWith(status);
    setOpenedEnd(p.expectedEndDate ? String(p.expectedEndDate).slice(0, 10) : '');
    const rows = ((p.links ?? []) as ProjectLink[]).map((link) => linkRow(link.name, link.url));
    setLinks(rows.length ? rows : [linkRow()]);
    setOpenedLinks(JSON.stringify(cleanLinks(rows)));
    setForm({
      name: p.name ?? '',
      nameAr: p.nameAr ?? '',
      code: p.code ?? '',
      description: p.description ?? '',
      ownerUserId: p.ownerUserId ?? '',
      clientId: p.clientId ?? '',
      status,
      priority: p.priority ?? 'MEDIUM',
      startDate: p.startDate ? String(p.startDate).slice(0, 10) : '',
      expectedEndDate: p.expectedEndDate ? String(p.expectedEndDate).slice(0, 10) : '',
    });
  }

  // §40 — the database refuses to move a project that already has invoices to
  // another client, because those invoices were issued against this pairing.
  const clientLocked = editing && (existing.data?._count?.invoices ?? 0) > 0;

  const changeLink = (key: number, patch: Partial<ProjectLink>) =>
    setLinks(links.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const removeLink = (key: number) => {
    const rest = links.filter((row) => row.key !== key);
    setLinks(rest.length ? rest : [linkRow()]);
  };
  // What is wrong with each row, as the API would refuse it; nothing for a blank one.
  const linkErrors = links.map((row) => {
    const name = row.name.trim();
    const url = row.url.trim();
    if (!name && !url) return { name: null, url: null };
    const problem = linkUrlProblem(url);
    return {
      name: name ? null : t('projects.linkNameRequired'),
      url:
        problem === 'required'
          ? t('projects.linkUrlRequired')
          : problem === 'password'
            ? t('projects.linkUrlPassword')
            : problem
              ? t('projects.linkUrlInvalid')
              : null,
    };
  });
  const linksWrong = linkErrors.some((e) => e.name || e.url);

  const save = useMutation({
    mutationFn: () => {
      const sentLinks = cleanLinks(links);
      const body = {
        ...form,
        code: form.code.trim(),
        // `null` clears the client; `undefined` would leave it untouched.
        clientId: form.clientId || (editing ? null : undefined),
        // Left as it opened, the status is not sent, and stays as it is now.
        status: editing && form.status === openedWith ? undefined : form.status,
        nameAr: form.nameAr || undefined,
        description: form.description || undefined,
        startDate: form.startDate || undefined,
        // Left as it opened, the end date is not sent: a contract that started meanwhile set it.
        expectedEndDate: editing && form.expectedEndDate === openedEnd ? undefined : form.expectedEndDate || undefined,
        // Left as they opened, the links are not sent, and stay as they are now.
        links: editing && JSON.stringify(sentLinks) === openedLinks ? undefined : sentLinks,
      };
      return editing ? api.patch<any>(`/projects/${id}`, body) : api.post<any>('/projects', body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['project', saved.id] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate(`/projects/${saved.id}`);
    },
    onError: (e: any) => {
      if (e?.code === 'DUPLICATE_RESOURCE' && e?.details?.field === 'code') {
        return toast.error(t('projects.codeTaken', { code: form.code.trim() }));
      }
      toast.error(e.message);
    },
  });

  const contracts = support.contracts.map((c) => c.number).join(', ');
  const until = formatDate(support.until, locale);
  const statusHint =
    form.status === 'SUPPORT'
      ? running
        ? t('projects.statusHintSupport', { contracts, date: until })
        : t('projects.statusHintSupportByHand')
      : form.status === 'COMPLETED'
        ? t('projects.statusHintCompleted')
        : form.status === 'CANCELLED' || form.status === 'ARCHIVED'
          ? running
            ? t('projects.closedContractNote', { contracts, date: until })
            : t(form.status === 'CANCELLED' ? 'projects.statusHintCancelled' : 'projects.statusHintArchived')
          : running && IN_PROGRESS_STATUSES.includes(form.status)
            ? t('projects.statusHintInProgress', { contracts, date: until })
            : undefined;

  const codeHint = editing
    ? undefined
    : nextCode.data?.after
      ? t('projects.codeFollows', { code: nextCode.data.after })
      : nextCode.data
        ? t('projects.codeNoSeries', { code: 'PRJ-001' })
        : undefined;

  return (
    <FormPage
      title={editing ? `${t('common.edit')} — ${form.code}` : t('projects.new')}
      backTo={editing ? `/projects/${id}` : '/projects'}
      backLabel={editing ? form.code || t('nav.projects') : t('nav.projects')}
      submitLabel={editing ? t('common.save') : t('common.create')}
      submitting={save.isPending}
      disabled={!form.name || !form.code.trim() || !form.ownerUserId}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => {
        setTried(true);
        if (!linksWrong) save.mutate();
      }}
    >
      <FormGrid>
        <Field label={`${t('common.name')} (EN)`} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
        </Field>
        <Field label={`${t('common.name')} (AR)`}>
          <Input dir="rtl" value={form.nameAr} onChange={(e) => setForm({ ...form, nameAr: e.target.value })} />
        </Field>
        <Field label={t('common.code')} required hint={codeHint}>
          <Input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            dir="ltr"
          />
        </Field>
        <Field label="Owner" required>
          <Select value={form.ownerUserId} onChange={(e) => setForm({ ...form, ownerUserId: e.target.value })}>
            <option value="">—</option>
            {(users ?? []).map((u) => (
              <option key={u.id} value={u.id}>{localized(u)}</option>
            ))}
          </Select>
        </Field>

        {can(P.CLIENTS_VIEW) && (
          <Field
            label={t('invoices.client')}
            hint={clientLocked ? t('projects.clientLocked') : t('clients.noClient')}
            className="sm:col-span-2"
          >
            <Select
              value={form.clientId}
              disabled={clientLocked}
              onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            >
              <option value="">{t('clients.noClient')}</option>
              {(clients.data ?? []).map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('common.description')} className="sm:col-span-2">
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label={t('common.status')} hint={statusHint}>
          <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {PROJECT_STATUSES.map((s) => {
              const locked = s === 'COMPLETED' && running;
              return (
                <option key={s} value={s} disabled={locked}>
                  {projectStatusLabel(s, locale)}
                  {locked ? ` — ${t('projects.completedLocked')}` : ''}
                </option>
              );
            })}
          </Select>
        </Field>
        <Field label={t('common.priority')}>
          <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => (
              <option key={p} value={p}>{enumLabel(p, locale)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('common.from')}>
          <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
        </Field>
        <Field label={t('common.to')}>
          <Input
            type="date"
            value={form.expectedEndDate}
            onChange={(e) => setForm({ ...form, expectedEndDate: e.target.value })}
          />
        </Field>

        <fieldset className="sm:col-span-2">
          <legend className="mb-1.5 text-body-sm font-medium text-[var(--text-secondary)]">{t('projects.links')}</legend>
          <ul className="space-y-4 sm:space-y-2">
            {links.map((row, i) => {
              const errors = tried ? linkErrors[i] : null;
              return (
                // On a phone the name and remove share the first line, and the URL takes the second.
                <li
                  key={row.key}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:grid-cols-[minmax(0,13rem)_minmax(0,1fr)_auto]"
                >
                  <div className="min-w-0">
                    <Input
                      aria-label={t('projects.linkName')}
                      aria-invalid={!!errors?.name}
                      placeholder={t('projects.linkNamePlaceholder')}
                      value={row.name}
                      maxLength={MAX_LINK_NAME_LENGTH}
                      dir="auto"
                      onChange={(e) => changeLink(row.key, { name: e.target.value })}
                    />
                    {errors?.name && (
                      <span className="mt-1 block text-caption text-error-fg dark:text-error-dark">{errors.name}</span>
                    )}
                  </div>
                  <div className="order-last col-span-2 min-w-0 sm:order-none sm:col-span-1">
                    <Input
                      aria-label={t('projects.linkUrl')}
                      aria-invalid={!!errors?.url}
                      placeholder="https://admin.example.com"
                      value={row.url}
                      maxLength={MAX_LINK_URL_LENGTH}
                      dir="ltr"
                      inputMode="url"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      onChange={(e) => changeLink(row.key, { url: e.target.value })}
                    />
                    {errors?.url && (
                      <span className="mt-1 block text-caption text-error-fg dark:text-error-dark">{errors.url}</span>
                    )}
                  </div>
                  <Button
                    icon={<Trash2 className="h-4 w-4" />}
                    aria-label={t('projects.removeLink')}
                    title={t('projects.removeLink')}
                    disabled={links.length === 1 && !row.name && !row.url}
                    onClick={() => removeLink(row.key)}
                  />
                </li>
              );
            })}
          </ul>
          <Button
            className="mt-2"
            size="sm"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setLinks([...links, linkRow()])}
          >
            {t('projects.addLink')}
          </Button>
          <p className="mt-1.5 text-caption text-faint">{t('projects.linksHint')}</p>
        </fieldset>
      </FormGrid>
    </FormPage>
  );
}
